import React, { useState } from 'react';
import { driverApi, DriverMe, DriverTopup } from '../../services/driverApi';
import { DriverCopy, formatMoney, MONTHLY_SUBSCRIPTION } from './driverI18n';
import { DriverHeader } from './DriverHeader';
import kaspiPayLogo from '../../assets/pay/kaspi-pay.png';

interface Props {
  t: DriverCopy;
  profile: DriverMe;
  payNotice?: 'ok' | 'fail' | 'cancel' | null;
  onBack: () => void;
  onPaid?: () => void;
}

function topupStatus(status: string) {
  const map: Record<string, string> = {
    SUCCESS: 'Успешно',
    COMPLETED: 'Выполнено',
    PENDING: 'В обработке',
    FAILED: 'Ошибка',
  };
  return map[status] || status;
}

export const DriverBalancePage: React.FC<Props> = ({ t, profile, payNotice, onBack, onPaid }) => {
  const [busy, setBusy] = useState<'hcb' | 'kaspi' | null>(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const parkPays = profile.billing?.payer === 'park';
  const paid = profile.billing?.status === 'paid' || parkPays;
  const cancelled = profile.billing?.status === 'voided';
  const canPay = !paid && !cancelled;
  const topups: DriverTopup[] = profile.topups || [];
  const amount = Number(profile.billing?.amount || 0) || MONTHLY_SUBSCRIPTION;

  const submitKaspiForm = (action: string, fields: Record<string, string>) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;
    form.acceptCharset = 'UTF-8';
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  const startHomeCredit = async () => {
    if (busy || sent) return;
    setBusy('hcb');
    setError('');
    try {
      const started = await driverApi.startProcardPayment();
      if (started.mocked) {
        setSent(true);
        onPaid?.();
        return;
      }
      if (started.url) {
        window.location.assign(started.url);
        return;
      }
      setError(t.payError);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : t.payError;
      setError(message);
    } finally {
      setBusy(null);
    }
  };

  const startKaspi = async () => {
    if (busy || sent) return;
    setBusy('kaspi');
    setError('');
    try {
      const started = await driverApi.startKaspiPayment({ qr: false });
      if (started.mocked) {
        setSent(true);
        onPaid?.();
        return;
      }
      if (started.url) {
        window.location.assign(started.url);
        return;
      }
      submitKaspiForm(started.action, started.form);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : t.payError;
      setError(message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="app-container driver-cabinet">
      <DriverHeader t={t} title={t.balance} onBack={onBack} />
      <main className="main-content driver-main">
        {payNotice === 'ok' ? <p className="driver-pay-sent">{t.payOk}</p> : null}
        {payNotice === 'fail' ? <p className="driver-pay-error">{t.payFail}</p> : null}
        {payNotice === 'cancel' ? <p className="driver-pay-error">{t.payCancel}</p> : null}
        {sent ? <p className="driver-pay-sent">{t.paySent}</p> : null}
        {error ? <p className="driver-pay-error">{error}</p> : null}

        <div className="driver-balance-simple driver-tariff">
          <div className="driver-tariff-row">
            <span>{t.tariff}</span>
            <strong>{t.tariffName}</strong>
          </div>
          <div className="driver-tariff-row">
            <span>{t.tariffPay}</span>
            <strong>
              {formatMoney(amount)} ₸ {t.tariffPerMonth}
            </strong>
          </div>
          <div className="driver-tariff-row">
            <span>{t.documentsCount}</span>
            <strong>{t.documentsUnlimited}</strong>
          </div>
        </div>

        <div className="driver-balance-simple">
          <p className="city">{t.balance}</p>
          <div className="amount">{formatMoney(amount)} ₸</div>
          {paid ? <p className="city">{parkPays ? t.parkPaysEdo : t.paid}</p> : null}
          {cancelled ? <p className="city">{t.subscriptionCancelled}</p> : null}
          {canPay && (
            <div className="driver-pay-methods">
              <button
                type="button"
                className="driver-kaspi-img-btn"
                disabled={Boolean(busy) || sent}
                onClick={startKaspi}
                aria-label={t.payKaspi}
              >
                <img src={kaspiPayLogo} alt={t.payKaspi} />
              </button>
              <button
                type="button"
                className="driver-pay-btn driver-pay-btn-secondary"
                disabled={Boolean(busy) || sent}
                onClick={startHomeCredit}
              >
                {t.payHomeHint}
              </button>
            </div>
          )}
        </div>

        <h3 className="driver-history-title">{t.topUpHistory}</h3>
        {topups.length === 0 ? (
          <div className="no-documents">{t.noTopups}</div>
        ) : (
          <div className="driver-topup-list">
            {topups.map((item) => (
              <div key={item.id} className="driver-topup-row">
                <div>
                  <div className="name">{t.topUp}</div>
                  <div className="city">{new Date(item.createdAt).toLocaleString('ru-RU')}</div>
                </div>
                <div className="tx-amount">
                  <strong>+{formatMoney(item.amount)} ₸</strong>
                  <span>{topupStatus(item.status)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
